import React from 'react'
import PropTypes from 'prop-types'
import undefsafe from 'undefsafe'
import raf from 'raf'
import VisibilitySensor from 'react-visibility-sensor'

import axiosVerbose from '../../../common/utils/axios/axiosVerbose'
import { logError } from '../../../common/utils/errors'
import { getMinutesSecondsTime } from '../../../common/utils/time'
import Waveform from './waveform'
import PlayIcon from '../icons/play'
import PauseIcon from '../icons/pause'
import SpinnerIcon from '../icons/spinner'

import styles from './audioPlayerLarge.module.css'

export default class AudioPlayerLarge extends React.Component {
  static propTypes = {
    item: PropTypes.object.isRequired,
    onPlay: PropTypes.func,
    isActive: PropTypes.bool
  }

  static defaultProps = {
    onPlay: () => {}
  }

  constructor (props) {
    super(props)

    this.state = {
      playing: false,
      bufferProgress: 0,
      playBarProgress: 0,
      expectedPlayBarProgress: null,
      dragging: false,
      loading: false,
      seen: false,
      waveformData: null,
      playPromise: null
    }

    this.togglePlay = this.togglePlay.bind(this)
    this.onAudioBuffer = this.onAudioBuffer.bind(this)
    this.onAudioProgress = this.onAudioProgress.bind(this)
    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onSeeked = this.onSeeked.bind(this)
    this.onDurationChange = this.onDurationChange.bind(this)
    this.onPlay = this.onPlay.bind(this)
    this.onPause = this.onPause.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.onElementVisible = this.onElementVisible.bind(this)
    this.setAudioElementRef = this.setAudioElementRef.bind(this)
    this.setWaveformContainerElementRef = this.setWaveformContainerElementRef.bind(this)
  }

  componentDidUpdate (prevProps) {
    if (this.audioElement) {
      if (prevProps.isActive && !this.props.isActive) {
        this.tryPause()
      }
    }
  }

  onElementVisible (isVisible) {
    const { item } = this.props
    const { seen } = this.state
    if (!seen && isVisible) {
      this.setState({ seen: true })
      // We get a url that looks like the following: https://0.s3.envato.com/files/88545/preview.mp3?foo=bar
      // This regex will extract the 88545/preview.mp3 part from it and send that to tsunami
      const mp3UrlRegex = /^.*files\/(.*\.mp3)/g
      const mp3Url = undefsafe(item, 'previews.icon_with_audio_preview.mp3_url')
      const mp3PathGroup = mp3UrlRegex.exec(mp3Url)
      let mp3Path
      if (mp3PathGroup && mp3PathGroup.length >= 1) {
        mp3Path = mp3PathGroup[1]
      }

      axiosVerbose
        .get(
          `https://waveform-discovery.envato.com/tsunami/${mp3Path}`
        )
        .then(response => {
          this.setState({ waveformData: response.data })
        })
        .catch(logError)
    }
  }

  onPause () {
    this.setState({ playing: false })
  }

  onPlay () {
    this.props.onPlay()
    this.setState({ playing: true })
    raf(this.onAudioProgress)
  }

  tryPause () {
    if (this.state.playPromise && this.audioElement) {
      this.state.playPromise.then(() => {
        this.audioElement.pause()
      })
    }
  }

  tryPlay () {
    if (this.audioElement) {
      /*
        For some old browsers (Safari 9) play() does not return promise but undefined.
        Let's handle that case by returning resolved promise instead.
       */
      return this.audioElement.play() || Promise.resolve()
    }
  }

  onDurationChange () {
    if (this.state.expectedPlayBarProgress) {
      this.audioElement.currentTime = this.state.expectedPlayBarProgress * this.audioElement.duration
    }
  }

  onSeeked () {
    this.showLoading()
  }

  onMouseDown (event) {
    const progress = event.nativeEvent.offsetX / this.waveformContainerElement.clientWidth
    this.setState({ dragging: true, playBarProgress: progress })
  }

  onMouseUp (event) {
    const { playBarProgress } = this.state

    this.setState({ dragging: false })

    if (this.audioElement.duration) {
      this.audioElement.currentTime = playBarProgress * this.audioElement.duration
      this.setState({
        playPromise: this.tryPlay()
      })

      this.showLoading()
    } else {
      this.setState({
        expectedPlayBarProgress: playBarProgress
      }, () => {
        this.setState({
          playPromise: this.tryPlay()
        })
        this.showLoading()
      })
    }
  }

  onMouseMove (event) {
    const { dragging } = this.state
    if (dragging) {
      this.setState({ playBarProgress: event.nativeEvent.offsetX / this.waveformContainerElement.clientWidth })
    }
  }

  onMouseLeave (event) {
    this.setState({
      dragging: false
    })
  }

  onAudioBuffer () {
    if (this.audioElement.buffered.length > 0 && this.audioElement.duration) {
      let percentLoaded = this.audioElement.buffered.end(this.audioElement.buffered.length - 1) / this.audioElement.duration
      if (percentLoaded > 1) {
        percentLoaded = 1
      }
      this.setState({ bufferProgress: percentLoaded })
    }
  }

  onAudioProgress () {
    if (this.state.playing) {
      if (!this.state.dragging && this.audioElement) {
        this.setState({ playBarProgress: this.audioElement.currentTime / this.audioElement.duration })
      }
      raf(this.onAudioProgress)
    }
  }

  showLoading () {
    if (this.audioElement.seeking || this.audioElement.readyState < 3) {
      this.setState({ loading: true })
    } else {
      this.setState({ loading: false })
    }
  }

  togglePlay () {
    const { playing } = this.state
    if (this.audioElement) {
      const isTrulyPlaying = this.audioElement.currentTime > 0 &&
        !this.audioElement.paused &&
        !this.audioElement.ended &&
        this.audioElement.readyState > 2
      if (playing && isTrulyPlaying) {
        this.tryPause()
      } else if (!isTrulyPlaying) {
        this.setState({
          playPromise: this.tryPlay()
        })
        this.showLoading()
      }
    }
  }

  setAudioElementRef (audioElement) {
    this.audioElement = audioElement
  }

  setWaveformContainerElementRef (waveformElement) {
    this.waveformContainerElement = waveformElement
  }

  render () {
    const {
      playing,
      loading,
      seen,
      waveformData,
      bufferProgress,
      playBarProgress
    } = this.state
    const { item } = this.props
    const playBarProgressStyles = {
      width: `${playBarProgress * 100}%`,
      height: '100%',
      position: 'absolute',
      boxShadow: '2px 0px 0px #000',
      display: playBarProgress > 0 ? null : 'none'
    }
    let buttonIcon = null
    if (loading) {
      buttonIcon = <SpinnerIcon className={styles.spinningIcon} />
    } else {
      if (playing) {
        buttonIcon = <PauseIcon className={styles.icon} />
      } else {
        buttonIcon = <PlayIcon className={styles.icon} />
      }
    }

    const mp3Url = undefsafe(item, 'previews.icon_with_audio_preview.mp3_url')

    return (
      <div className={styles.spaceBetween}>
        <VisibilitySensor onChange={this.onElementVisible} active={!seen} />
        <audio
          ref={this.setAudioElementRef}
          preload='none'
          onPlay={this.onPlay}
          onPause={this.onPause}
          onDurationChange={this.onDurationChange}
          onProgress={this.onAudioBuffer}
          onSeeked={this.onSeeked}
          onCanPlay={this.onSeeked}
        >
          <source src={mp3Url} type='audio/mpeg' />
        </audio>
        <button onClick={this.togglePlay} className='fds-c-button -color-transparent'>
          {buttonIcon}
        </button>
        { waveformData &&
          <div ref={this.setWaveformContainerElementRef} className={styles.waveformContainer} onMouseDown={this.onMouseDown} onMouseUp={this.onMouseUp} onMouseMove={this.onMouseMove} onMouseLeave={this.onMouseLeave}>
            <Waveform data={waveformData} colour='#aaa' progress={1} />
            <Waveform data={waveformData} colour='#7e7e7e' progress={bufferProgress} />
            <Waveform data={waveformData} colour='#7aa93c' progress={playBarProgress} />
            <div style={playBarProgressStyles}>
              <div className={styles.timeContainer}>
                {this.audioElement ? getMinutesSecondsTime(this.audioElement.currentTime) : ''}
              </div>
            </div>
          </div>
        }
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./client/components/audio/audioPlayerLarge.js